/**
 * React Query hook for updating series node positions
 *
 * Mutation hook for persisting React Flow node positions to the backend.
 * Uses optimistic updates to maintain UI responsiveness while saving to API.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { updateSeriesPositions } from '@/api/series'
import { seriesKeys } from '@/api/queryKeys'
import type { UpdateSeriesPositionsDto } from '@/api/series'

interface UpdateSeriesPositionsParams {
  seriesId: string
  data: UpdateSeriesPositionsDto
}

/**
 * Hook for updating series node positions
 *
 * Persists React Flow node coordinates to the backend.
 * Invalidates series detail cache on success to ensure consistency.
 *
 * @example
 * ```tsx
 * const updatePositions = useUpdateSeriesPositions();
 *
 * updatePositions.mutate({
 *   seriesId: 'series-uuid',
 *   data: {
 *     positions: [
 *       { seriesMoveId: 'sm-1', positionX: 100, positionY: 200 },
 *       { seriesMoveId: 'sm-2', positionX: 300, positionY: 200 },
 *     ],
 *   },
 * });
 * ```
 */
interface UseUpdateSeriesPositionsOptions {
  /**
   * When true, do NOT invalidate seriesKeys.detail on success.
   * Useful for the WebView embed canvas where the local React Flow state is
   * already authoritative for displayed positions and a refetch would only
   * cause a wasted network roundtrip + canvas re-render.
   */
  skipInvalidation?: boolean
}

export function useUpdateSeriesPositions(options: UseUpdateSeriesPositionsOptions = {}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ seriesId, data }: UpdateSeriesPositionsParams) =>
      updateSeriesPositions(seriesId, data),

    onSuccess: (_updatedSeries, variables) => {
      if (options.skipInvalidation) return
      // Invalidate series detail to refetch with updated positions
      queryClient.invalidateQueries({
        queryKey: seriesKeys.detail(variables.seriesId),
      })
    },

    onError: (error: Error, _variables) => {
      console.error('Failed to update series positions:', error)
      toast({
        title: 'Failed to save layout',
        description:
          'Could not save node positions. Your changes may be lost when you leave this page.',
        variant: 'destructive',
      })
    },
  })
}
